import type { BranchChangesets } from '@festina/configuration-general-types/configuration'
import { LayerKey } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useState } from 'react'

import { useMetaModelContext } from '../../../../context/metaModelContext'
import type { OperationWithSourceInfo } from '../../../../uiModels/types'
import type { ChangeOperation } from '../../../../uiModels/workspace/types'
import { useOperationsContext } from '../../../../workspace/operationsContext'
import { loadTypedDataFromFile } from '../../../branchCreator/helpers'

export const useUploadWorkspacePackage = () => {
  const { onAddOrUpdateOperation } = useOperationsContext()
  const [parsedPackage, setParsedPackage] =
    useState<Optional<OperationWithSourceInfo<ChangeOperation>[]>>()
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<Optional<string>>()
  const translate = useTranslate()
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name // FIXME fix this once workspaces support blocks

  const parseFile = useCallback(
    async (file: File) => {
      setError(null)
      setParsedPackage(null)

      try {
        const pkg = await loadTypedDataFromFile<BranchChangesets>(
          file,
          'BranchChangesets',
        )

        const operations = (pkg.branchChangesets ?? [])
          .flatMap(
            (branchChangeset) =>
              branchChangeset?.changeset?.operations?.map(
                (operation) =>
                  ({
                    internalOperation: operation as ChangeOperation,
                    isDraft: true,
                    actions: [],
                    changesetHistory: branchChangeset.changesetHistory,
                    layer: new LayerKey({
                      layerId: branchChangeset.changeset?.layer ?? null,
                      tenantId: branchChangeset.changeset?.tenantId ?? null,
                    }),
                    block: branchChangeset.block ?? rootBlock,
                    annulOperations: [],
                  }) satisfies OperationWithSourceInfo<ChangeOperation>,
              ) ?? [],
          )
          .filter(notEmpty)

        setParsedPackage(operations)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to parse the file.',
        )
      }
    },
    [rootBlock],
  )

  const upload = useCallback(async (): Promise<boolean> => {
    if (!parsedPackage) {
      return false
    }

    setIsUploading(true)
    setError(null)

    try {
      for (const operation of parsedPackage) {
        await onAddOrUpdateOperation({
          operation: operation.internalOperation,
          layer: operation.layer,
          block: operation.block,
          shouldOpenUnifiedBranchWorkspace: false,
        })
      }
      return true
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('uploadWorkspacePackage.error'),
      )
      return false
    } finally {
      setIsUploading(false)
    }
  }, [parsedPackage, onAddOrUpdateOperation, translate])

  const reset = useCallback(() => {
    setParsedPackage(null)
    setError(null)
    setIsUploading(false)
  }, [])

  return { parsedPackage, parseFile, upload, isUploading, error, reset }
}
